import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, TrendingDown, Wallet, Plus, ArrowUpRight, ArrowDownRight, Sparkles, Percent, Receipt, Users, AlertTriangle, Download, RefreshCw, ChevronDown,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n, categoryLabel } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import UploadInvoiceModal from "@/components/UploadInvoiceModal";
import BulkUploadModal from "@/components/BulkUploadModal";
import TrialBanner from "@/components/TrialBanner";
import BasicPlanBanner from "@/components/BasicPlanBanner";
import PriceAlertsBanner from "@/components/dashboard/PriceAlertsBanner";
import AlbaranPriceAlertsBanner from "@/components/dashboard/AlbaranPriceAlertsBanner";
import CategoryBreakdown from "@/components/dashboard/CategoryBreakdown";
import ChartCard from "@/components/dashboard/ChartCard";

const COLORS = ["#2C4C3B", "#C98A3A", "#A44C40", "#3F5C7A", "#6B8F71", "#7A5C44", "#8A5A9E", "#3A6B53", "#5E6F58", "#1F382A"];

// Reusable chart styles — hoisted out of render to avoid creating fresh objects each pass.
const CHART_MARGIN = { top: 10, right: 12, left: 0, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: "#8a8a86" };
const TOOLTIP_STYLE = { background: "white", border: "1px solid #e5e5e2", borderRadius: 8, fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const LINE_DOT = { r: 3 };

function formatMoney(value, currency = "EUR") {
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always" }).format(value || 0);
  } catch {
    return `${(value || 0).toFixed(2)} ${currency}`;
  }
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const baseCurrency = user?.base_currency || "EUR";
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const [stats, setStats] = useState({ totals: { income: 0, expense: 0, balance: 0 }, by_category: [], by_month: [] });
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [modalDefaults, setModalDefaults] = useState({ type: "expense", categoryKind: null });
  const [priceAlerts, setPriceAlerts] = useState({ count: 0, products: [], threshold_pct: 10 });
  const [quarter, setQuarter] = useState({ year: currentYear, quarters: [currentQuarter] });
  const [quarterData, setQuarterData] = useState(null);
  // Modo "mensual" del resumen. Cuando activo, cargamos /stats/monthly con la lista
  // de meses seleccionados (1..12). Cuando inactivo, seguimos usando /stats/quarterly.
  const [periodMode, setPeriodMode] = useState("quarterly"); // "quarterly" | "monthly"
  const [monthsSel, setMonthsSel] = useState([now.getMonth() + 1]);
  const [monthlyData, setMonthlyData] = useState(null);
  // Comparación año vs año en modo mensual. Cuando `compareYears.length >= 2`,
  // se disparan N llamadas en paralelo a /stats/monthly y se muestra una tabla
  // con columnas por (mes, año). Default = solo el año activo.
  const [compareYears, setCompareYears] = useState([currentYear]);
  const [monthlyDataByYear, setMonthlyDataByYear] = useState({});
  const [ratios, setRatios] = useState({ year: currentYear, months: [] });
  const [uploadPace, setUploadPace] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  // Summary year filter. Defaults to current year so the KPIs & "Ingresos vs
  // Gastos" chart show the current fiscal year at load. Setting to "" (Total)
  // returns to the historic all-time aggregation.
  const [summaryYear, setSummaryYear] = useState(String(currentYear));

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const quartersParam = (quarter.quarters || []).slice().sort((a, b) => a - b).join(",");
      const monthsParam = (monthsSel || []).slice().sort((a, b) => a - b).join(",");
      const summaryParams = summaryYear ? { year: Number(summaryYear) } : {};
      const [s, c, r, q, m, rt, up, pa] = await Promise.all([
        api.get("/stats/summary", { params: summaryParams }),
        api.get("/categories"),
        api.get("/invoices", { params: { limit: 6 } }),
        api.get("/stats/quarterly", { params: { year: quarter.year, quarters: quartersParam } }),
        periodMode === "monthly"
          ? api.get("/stats/monthly", { params: { year: quarter.year, months: monthsParam } })
          : Promise.resolve({ data: null }),
        api.get("/stats/ratios", { params: { year: quarter.year } }),
        api.get("/stats/upload-pace"),
        api.get("/stats/price-alerts", { params: summaryYear ? { year: Number(summaryYear) } : {} }),
      ]);
      setStats(s.data);
      setCategories(c.data);
      setRecent(r.data);
      setQuarterData(q.data);
      setMonthlyData(m.data);
      setRatios(rt.data);
      setUploadPace(up.data);
      setPriceAlerts(pa.data);

      // Comparación multi-año (opcional). Solo si el usuario ha seleccionado
      // 2+ años y estamos en modo mensual. Se dispara aparte para no penalizar
      // la carga inicial en el caso común (un solo año).
      if (periodMode === "monthly" && compareYears.length >= 2) {
        try {
          const results = await Promise.all(
            compareYears.map((yr) =>
              api.get("/stats/monthly", { params: { year: yr, months: monthsParam } })
                .then((res) => [yr, res.data])
            )
          );
          setMonthlyDataByYear(Object.fromEntries(results));
        } catch {
          setMonthlyDataByYear({});
        }
      } else {
        setMonthlyDataByYear({});
      }
      setLastRefreshed(new Date());
    } catch (err) {
      // Surface backend failures instead of silently showing €0 everywhere
      // (LOW→MED code review finding). The Refresh button had its own toast,
      // but the initial mount path did not.
      toast.error(formatApiError(err));
    } finally {
      setRefreshing(false);
    }
  }, [quarter.year, quarter.quarters, monthsSel, periodMode, summaryYear, compareYears]);

  useEffect(() => {
    load();
  }, [load]);

  const catMap = Object.fromEntries(categories.map((c) => [c.name, c]));
  // Pseudo-category "salaries" is synthesized by the backend from salary_entries
  // (payroll). It has no row in db.categories, so we inject a friendly label.
  const SALARIES_LABEL = { name: "salaries", label_es: "Salarios", label_en: "Salaries" };
  // Stable Recharts tooltip renderer. Wrapped in useCallback so React (and
  // eslint no-unstable-nested-components) don't complain about a fresh
  // component identity every render.
  const renderRatiosTooltip = useCallback(
    (props) => (
      <RatiosTooltip
        {...props}
        lang={lang}
        t={t}
        formatMoney={formatMoney}
        baseCurrency={baseCurrency}
      />
    ),
    [lang, t, formatMoney, baseCurrency],
  );
  const pieData = stats.by_category.map((c) => ({
    name: categoryLabel(
      c.category === "salaries" ? SALARIES_LABEL : (catMap[c.category] || { name: c.category }),
      lang,
    ),
    value: c.total,
    raw: c.category,
  }));

  return (
    <div className="space-y-8">
      {/* Trial countdown banner — only visible while `is_trialing`. Escalates
          from soft-info (green) → amber (7d) → critical (≤3d). */}
      <TrialBanner user={user} lang={lang} />

      {/* Iter 82 — recordatorio de las 5 áreas bloqueadas en plan Basic. */}
      <BasicPlanBanner user={user} lang={lang} />

      {/* Real-price overcharge banner (invoice > albarán). */}
      <AlbaranPriceAlertsBanner lang={lang} formatMoney={(v, c) => formatMoney(v, c || baseCurrency)} />

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          {user?.company_logo_url && (
            <img
              src={user.company_logo_url}
              alt={user.company_name || user.name || "logo"}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-contain bg-white p-1 border border-[var(--border-color)] shadow-sm"
              data-testid="dashboard-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
              {user?.company_name || t("dashboard")}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none">
              {lang === "es" ? "Hola" : "Hi"}, {user?.name}.
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Year selector — filters KPIs + Ingresos vs Gastos chart.
              "Total" = all-time historic aggregation. Available years are the
              current year and the 4 preceding ones — enough for most tenants
              without spamming the dropdown. */}
          <select
            value={summaryYear}
            onChange={(e) => setSummaryYear(e.target.value)}
            className="h-11 px-3 rounded-md border border-[var(--border-color)] bg-[var(--surface)] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30"
            data-testid="dashboard-year-selector"
            title={lang === "es" ? "Filtrar KPIs por año" : "Filter KPIs by year"}
          >
            {[currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4].map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
            <option value="">{lang === "es" ? "Total" : "All time"}</option>
          </select>
          <Button
            onClick={async () => {
              try {
                await load();
                toast.success(lang === "es" ? "Datos actualizados" : "Data refreshed");
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            variant="outline"
            disabled={refreshing}
            data-testid="dashboard-refresh-btn"
            title={lastRefreshed
              ? `${lang === "es" ? "Última actualización" : "Last refresh"}: ${lastRefreshed.toLocaleTimeString()}`
              : (lang === "es" ? "Actualizar datos" : "Refresh data")}
            className="h-12 px-5 text-sm font-semibold shadow-sm"
          >
            <RefreshCw size={18} strokeWidth={2} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {lang === "es" ? "Actualizar" : "Refresh"}
          </Button>
          <Button
            onClick={() => { setModalDefaults({ type: "income", categoryKind: null }); setModalOpen(true); }}
            variant="outline"
            data-testid="dashboard-upload-income-btn"
            className="h-12 px-5 text-sm font-semibold border-2 border-[var(--status-positive)]/40 text-[var(--status-positive)] hover:bg-[var(--status-positive-bg)] shadow-sm"
          >
            <TrendingUp size={18} strokeWidth={2} className="mr-2" /> {t("uploadIncome")}
          </Button>
          <Button
            onClick={() => { setModalDefaults({ type: "expense", categoryKind: "salary" }); setModalOpen(true); }}
            variant="outline"
            data-testid="dashboard-upload-salary-btn"
            className="h-12 px-5 text-sm font-semibold border-2 shadow-sm"
          >
            <Users size={18} strokeWidth={2} className="mr-2" /> {t("uploadSalary")}
          </Button>
          <Button
            onClick={() => setBulkOpen(true)}
            variant="outline"
            data-testid="bulk-upload-btn"
            className="h-12 px-5 text-sm font-semibold border-2 border-[var(--brand-accent)]/40 text-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/10 shadow-sm"
          >
            <Sparkles size={18} strokeWidth={2} className="mr-2" /> {t("bulkUpload")}
          </Button>
          <Button
            onClick={() => { setModalDefaults({ type: "expense", categoryKind: null }); setModalOpen(true); }}
            data-testid="upload-invoice-btn"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white h-12 px-6 text-sm font-semibold shadow-md hover:shadow-lg transition-shadow"
          >
            <Plus size={20} strokeWidth={2.5} className="mr-2" /> {t("uploadInvoice")}
          </Button>
        </div>
      </div>

      <UploadPaceBanner data={uploadPace} lang={lang} />

      {/* KPI cards — overall balance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KpiCard
          label={t("netBalance")}
          value={formatMoney(stats.totals.balance, baseCurrency)}
          accent="primary"
          icon={Wallet}
          testid="kpi-balance"
        />
        <KpiCard
          label={t("ivaBalance")}
          value={formatMoney(stats.totals.iva_balance || 0, baseCurrency)}
          accent={
            (stats.totals.iva_balance || 0) > 0 ? "negative" : (stats.totals.iva_balance || 0) < 0 ? "positive" : "primary"
          }
          testid="kpi-iva-balance"
          help={t("ivaBalanceHelp")}
        />
      </div>

      {/* Income breakdown */}
      <div>
        <h2 className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-3 font-semibold">
          {t("income")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            label={t("incomeBase")}
            value={formatMoney(stats.totals.income_base || 0, baseCurrency)}
            accent="positive"
            icon={Receipt}
            testid="kpi-income-base"
          />
          <KpiCard
            label={t("incomeIva")}
            value={formatMoney(stats.totals.income_iva || 0, baseCurrency)}
            accent="positive"
            testid="kpi-income-iva"
          />
          <KpiCard
            label={t("incomeTotal")}
            value={formatMoney(stats.totals.income, baseCurrency)}
            accent="positive"
            icon={TrendingUp}
            testid="kpi-income"
          />
        </div>
      </div>

      {/* Expense breakdown */}
      <div>
        <h2 className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-3 font-semibold">
          {t("expense")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            label={t("expenseBase")}
            value={formatMoney(stats.totals.expense_base || 0, baseCurrency)}
            accent="negative"
            icon={Receipt}
            testid="kpi-expense-base"
            subtitle={
              stats.totals.salaries_total > 0
                ? (lang === "es"
                    ? `Incluye ${formatMoney(stats.totals.salaries_total, baseCurrency)} de salarios`
                    : `Includes ${formatMoney(stats.totals.salaries_total, baseCurrency)} in salaries`)
                : (lang === "es" ? "Base gastos + salarios" : "Expense base + salaries")
            }
            help={lang === "es"
              ? "Gasto Base = Base imponible de gastos + Salarios (los salarios no llevan IVA)"
              : "Expense Base = Net expenses + Salaries (salaries have no VAT)"}
          />
          <KpiCard
            label={t("expenseIva")}
            value={formatMoney(stats.totals.expense_iva || 0, baseCurrency)}
            accent="negative"
            testid="kpi-expense-iva"
            subtitle={lang === "es" ? "Solo de facturas de gastos" : "From expense invoices only"}
            help={lang === "es"
              ? "IVA Soportado — sólo el IVA de las facturas de gastos. Los salarios no llevan IVA."
              : "Input VAT — only the VAT from expense invoices. Salaries have no VAT."}
          />
          <KpiCard
            label={t("expenseTotal")}
            value={formatMoney(stats.totals.expense, baseCurrency)}
            accent="negative"
            icon={TrendingDown}
            testid="kpi-expense"
            subtitle={lang === "es" ? "Base + Salarios + IVA" : "Base + Salaries + VAT"}
            help={lang === "es"
              ? "Total Pagado = Gasto Base (con salarios) + IVA Soportado"
              : "Total Paid = Expense Base (with salaries) + Input VAT"}
          />
        </div>
      </div>

      {/* Aviso importante: subidas >10% de precios de productos este año.
          Aparece antes de cualquier KPI para que salte a la vista. */}
      <PriceAlertsBanner alerts={priceAlerts} lang={lang} formatMoney={formatMoney} onDismiss={load} />

      {/* Quarterly / Monthly summary + restaurant ratios */}
      {quarterData && (
        <PeriodSummary
          quarterData={quarterData}
          monthlyData={monthlyData}
          monthlyDataByYear={monthlyDataByYear}
          year={quarter.year}
          quarters={quarter.quarters}
          months={monthsSel}
          compareYears={compareYears}
          mode={periodMode}
          onModeChange={setPeriodMode}
          onQuartersChange={(next) => setQuarter(next)}
          onMonthsChange={setMonthsSel}
          onCompareYearsChange={setCompareYears}
          baseCurrency={baseCurrency}
          formatMoney={formatMoney}
        />
      )}

      {/* Monthly ratios chart */}
      <ChartCard
        testid="chart-ratios"
        title={t("monthlyRatios")}
        subtitle={`${ratios.year}`}
        lang={lang}
        exportUrl="/stats/ratios/export"
        exportParams={{ year: ratios.year }}
        exportFilename={`panel-ratios-${ratios.year}.xlsx`}
        renderChart={(height) => (
          <div style={{ height: typeof height === "number" ? `${height}px` : height, minHeight: 240 }}>
            {ratios.months && ratios.months.some((m) => m.food_cost_pct != null || m.salary_pct != null || m.revenue_base > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ratios.months} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eaeae6" vertical={false} />
                  <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis
                    yAxisId="pct"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <YAxis
                    yAxisId="rev"
                    orientation="right"
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    content={renderRatiosTooltip}
                  />
                  <Legend iconType="square" wrapperStyle={LEGEND_STYLE} />
                  <Line yAxisId="pct" type="monotone" dataKey="food_cost_pct" stroke="#C98A3A" strokeWidth={2} dot={LINE_DOT} name={t("foodCostPct")} connectNulls />
                  <Line yAxisId="pct" type="monotone" dataKey="salary_pct" stroke="#3F5C7A" strokeWidth={2} dot={LINE_DOT} name={t("salaryPct")} connectNulls />
                  <Line yAxisId="rev" type="monotone" dataKey="revenue_base" stroke="#2C4C3B" strokeWidth={2} dot={LINE_DOT} name={t("income")} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart text={lang === "es" ? "Sin datos aún. Sube ingresos y gastos de Food cost / Salarios." : "No data yet. Upload revenue and food-cost / labor expenses."} />
            )}
          </div>
        )}
      />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard
          className="lg:col-span-2"
          testid="chart-monthly"
          title={t("incomeVsExpense")}
          subtitle={t("monthly")}
          lang={lang}
          exportUrl="/stats/summary/export"
          exportParams={summaryYear ? { year: Number(summaryYear) } : {}}
          exportFilename={`panel-mensual-${summaryYear || "total"}.xlsx`}
          renderChart={(height) => (
            <div style={{ height: typeof height === "number" ? `${height}px` : height, minHeight: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={(stats.by_month || []).map((m) => ({
                    ...m,
                    profit: (m.income || 0) - (m.expense || 0),
                  }))}
                  margin={CHART_MARGIN}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eaeae6" vertical={false} />
                  <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v) => formatMoney(v, baseCurrency)}
                  />
                  <Legend iconType="square" wrapperStyle={LEGEND_STYLE} />
                  <Line type="monotone" dataKey="income" stroke="#2C4C3B" strokeWidth={2} dot={LINE_DOT} name={t("income")} />
                  <Line type="monotone" dataKey="expense" stroke="#A44C40" strokeWidth={2} dot={LINE_DOT} name={t("expense")} />
                  <Line type="monotone" dataKey="profit" stroke="#3F5C7A" strokeWidth={2} strokeDasharray="4 3" dot={LINE_DOT} name={t("profit")} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        />

        <ChartCard
          testid="chart-category"
          title={t("byCategory")}
          subtitle={t("expense")}
          lang={lang}
          exportUrl="/stats/summary/export"
          exportParams={summaryYear ? { year: Number(summaryYear) } : {}}
          exportFilename={`panel-categorias-${summaryYear || "total"}.xlsx`}
          renderChart={() => (
            <div className="max-h-96 overflow-y-auto">
              {pieData.length > 0 ? (
                <CategoryBreakdown
                  categories={pieData}
                  catMap={catMap}
                  lang={lang}
                  baseCurrency={baseCurrency}
                  formatMoney={formatMoney}
                  summaryYear={summaryYear}
                />
              ) : (
                <EmptyChart text={lang === "es" ? "Sin datos aún" : "No data yet"} />
              )}
            </div>
          )}
        />
      </div>

      {/* Top expenses */}
      <TopExpensesCard lang={lang} t={t} />

      <UploadInvoiceModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        categories={categories}
        defaultType={modalDefaults.type}
        defaultCategoryKind={modalDefaults.categoryKind}
        onCreated={load}
      />

      <BulkUploadModal
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onDone={load}
      />
    </div>
  );
}

function Card({ children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 card-lift ${className}`}
    >
      {children}
    </div>
  );
}

function CardHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
        {subtitle && (
          <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mt-0.5">{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}

function KpiCard({ label, value, accent, icon: Icon, testid, help, subtitle }) {
  const accentClass = {
    primary: "text-[var(--text-primary)]",
    positive: "text-[var(--status-positive)]",
    negative: "text-[var(--status-negative)]",
  }[accent];
  return (
    <div
      data-testid={testid}
      className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 card-lift"
      title={help || undefined}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)]">{label}</p>
        {Icon && <Icon size={16} strokeWidth={1.75} className="text-[var(--text-muted)]" />}
      </div>
      <p className={`font-display text-3xl sm:text-4xl font-bold tracking-tight font-mono-num ${accentClass}`}>
        {value}
      </p>
      {subtitle && (
        <p className="text-[10px] text-[var(--text-muted)] mt-1.5 leading-tight">{subtitle}</p>
      )}
    </div>
  );
}

function EmptyChart({ text }) {
  return (
    <div className="h-full grid place-items-center text-sm text-[var(--text-muted)]">{text}</div>
  );
}

const MONTH_LABELS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTH_LABELS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Tarjeta de resumen del periodo. Soporta dos modos:
 *   - `quarterly`: selecciona 1..4 trimestres del año → usa `/stats/quarterly`.
 *   - `monthly`: selecciona 1..12 meses del año → usa `/stats/monthly` y añade
 *     una tabla de comparación mes a mes cuando hay ≥ 2 meses seleccionados.
 * Ambos modos comparten las mismas MiniStats (Base, IVA repercutido, IVA
 * soportado, Balance IVA) y RatioCards (Food cost %, Salarios %).
 */
function PeriodSummary({
  quarterData,
  monthlyData,
  monthlyDataByYear,
  year,
  quarters,
  months,
  compareYears,
  mode,
  onModeChange,
  onQuartersChange,
  onMonthsChange,
  onCompareYearsChange,
  baseCurrency,
  formatMoney,
}) {
  const { t, lang } = useI18n();
  const monthNames = lang === "es" ? MONTH_LABELS_ES : MONTH_LABELS_EN;

  // Datos activos según el modo.
  const isMonthly = mode === "monthly";
  const activeData = isMonthly ? monthlyData?.aggregate : quarterData;
  const { totals = {}, ratios = {} } = activeData || {};

  // Health pill: target Food Cost 28-35%, Salary 25-35%.
  const pillColor = (pct, lo, hi) => {
    if (pct == null) return "text-[var(--text-muted)] bg-[var(--surface-hover)]";
    if (pct <= hi) return "text-[var(--status-positive)] bg-[var(--status-positive-bg)]";
    if (pct <= hi + 10) return "text-[var(--status-warning)] bg-[var(--status-warning-bg)]";
    return "text-[var(--status-negative)] bg-[var(--status-negative-bg)]";
  };

  // ---- Selectores ----
  const selectedQ = new Set(quarters || []);
  const toggleQuarter = (q) => {
    const next = new Set(selectedQ);
    if (next.has(q)) next.delete(q);
    else next.add(q);
    if (next.size === 0) next.add(q); // mantener siempre ≥ 1
    onQuartersChange({ year, quarters: Array.from(next).sort((a, b) => a - b) });
  };

  const selectedM = new Set(months || []);
  const toggleMonth = (m) => {
    const next = new Set(selectedM);
    if (next.has(m)) next.delete(m);
    else next.add(m);
    if (next.size === 0) next.add(m);
    onMonthsChange(Array.from(next).sort((a, b) => a - b));
  };

  const subtitleLabel = (() => {
    if (isMonthly) {
      if (selectedM.size === 12) return lang === "es" ? "Año completo" : "Full year";
      if (selectedM.size === 1) {
        const m = Array.from(selectedM)[0];
        return `${monthNames[m - 1]} ${year}`;
      }
      return Array.from(selectedM).sort((a, b) => a - b).map((m) => monthNames[m - 1]).join(" + ");
    }
    if (selectedQ.size === 4) return lang === "es" ? "Año completo" : "Full year";
    if (selectedQ.size === 1) return `Q${Array.from(selectedQ)[0]}`;
    return Array.from(selectedQ).sort((a, b) => a - b).map((q) => `Q${q}`).join(" + ");
  })();

  const dateRange = activeData ? `${activeData.start} → ${activeData.end}` : "";

  return (
    <Card testid="period-summary">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold tracking-tight">
            {isMonthly
              ? (lang === "es" ? "Resumen mensual" : "Monthly summary")
              : t("quarterlySummary")}
          </h3>
          <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mt-0.5">
            {subtitleLabel}
            {dateRange && ` · ${dateRange}`} · {t("ratiosOverBase")}
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs flex-wrap">
          {/* Tabs Trimestral / Mensual */}
          <div className="inline-flex rounded-md border border-[var(--border-color)] overflow-hidden">
            <button
              type="button"
              onClick={() => onModeChange("quarterly")}
              aria-pressed={!isMonthly}
              data-testid="period-mode-quarterly"
              className={`px-2.5 py-1 transition-colors border-r border-[var(--border-color)] ${
                !isMonthly
                  ? "bg-[var(--primary)] text-white"
                  : "bg-[var(--surface)] hover:bg-[var(--surface-hover)]"
              }`}
            >
              {lang === "es" ? "Trimestral" : "Quarterly"}
            </button>
            <button
              type="button"
              onClick={() => onModeChange("monthly")}
              aria-pressed={isMonthly}
              data-testid="period-mode-monthly"
              className={`px-2.5 py-1 transition-colors ${
                isMonthly
                  ? "bg-[var(--primary)] text-white"
                  : "bg-[var(--surface)] hover:bg-[var(--surface-hover)]"
              }`}
            >
              {lang === "es" ? "Mensual" : "Monthly"}
            </button>
          </div>

          <select
            value={year}
            onChange={(e) => {
              const y = Number(e.target.value);
              if (isMonthly) onMonthsChange(Array.from(selectedM));
              onQuartersChange({ year: y, quarters: Array.from(selectedQ) });
            }}
            data-testid="quarter-year"
            className="rounded border border-[var(--border-color)] bg-[var(--surface)] px-2 py-1 font-mono-num"
          >
            {Array.from({ length: 5 }, (_, i) => year - 2 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {!isMonthly && (
            <>
              <div className="inline-flex rounded-md border border-[var(--border-color)] overflow-hidden">
                {[1, 2, 3, 4].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => toggleQuarter(q)}
                    data-testid={`quarter-${q}`}
                    aria-pressed={selectedQ.has(q)}
                    className={`px-2.5 py-1 transition-colors border-r border-[var(--border-color)] last:border-r-0 ${
                      selectedQ.has(q)
                        ? "bg-[var(--primary)] text-white"
                        : "bg-[var(--surface)] hover:bg-[var(--surface-hover)]"
                    }`}
                  >
                    Q{q}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onQuartersChange({ year, quarters: [1, 2, 3, 4] })}
                data-testid="quarter-all-year"
                className="px-2.5 py-1 rounded border border-[var(--border-color)] bg-[var(--surface)] hover:bg-[var(--surface-hover)] transition-colors"
                title={lang === "es" ? "Seleccionar todo el año" : "Select full year"}
              >
                {lang === "es" ? "Todo" : "All"}
              </button>
            </>
          )}

          {isMonthly && (
            <>
              <div className="inline-flex rounded-md border border-[var(--border-color)] overflow-hidden flex-wrap">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMonth(m)}
                    data-testid={`month-${m}`}
                    aria-pressed={selectedM.has(m)}
                    className={`px-2 py-1 transition-colors border-r border-[var(--border-color)] last:border-r-0 ${
                      selectedM.has(m)
                        ? "bg-[var(--primary)] text-white"
                        : "bg-[var(--surface)] hover:bg-[var(--surface-hover)]"
                    }`}
                  >
                    {monthNames[m - 1]}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onMonthsChange(Array.from({ length: 12 }, (_, i) => i + 1))}
                data-testid="month-all-year"
                className="px-2.5 py-1 rounded border border-[var(--border-color)] bg-[var(--surface)] hover:bg-[var(--surface-hover)] transition-colors"
                title={lang === "es" ? "Seleccionar todo el año" : "Select full year"}
              >
                {lang === "es" ? "Todo" : "All"}
              </button>
              {/* Comparación año vs año: selector de 2+ años. Cuando se elige más
                  de un año se dispara `monthlyDataByYear` y se muestra la tabla
                  cruzada al final de la tarjeta. */}
              <MultiYearSelector
                current={year}
                selected={compareYears}
                onChange={onCompareYearsChange}
                lang={lang}
              />
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <MiniStat label={t("revenueBase")} value={formatMoney(ratios.revenue_base, baseCurrency)} testid="quarter-revenue" />
        <MiniStat
          label={lang === "es" ? "Gastos (base)" : "Expenses (net)"}
          value={formatMoney(totals.expense_base, baseCurrency)}
          testid="quarter-expense-base"
          accent="negative"
        />
        <MiniStat
          label={lang === "es" ? "Beneficio" : "Profit"}
          value={formatMoney((ratios.revenue_base || 0) - (totals.expense_base || 0), baseCurrency)}
          accent={((ratios.revenue_base || 0) - (totals.expense_base || 0)) >= 0 ? "positive" : "negative"}
          testid="quarter-profit"
        />
        <MiniStat
          label={lang === "es" ? "% Beneficio" : "Profit %"}
          value={
            (ratios.revenue_base || 0) > 0
              ? `${(((ratios.revenue_base - (totals.expense_base || 0)) / ratios.revenue_base) * 100).toFixed(1)}%`
              : "—"
          }
          accent={((ratios.revenue_base || 0) - (totals.expense_base || 0)) >= 0 ? "positive" : "negative"}
          testid="quarter-profit-pct"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mt-3">
        <MiniStat label={t("incomeIva")} value={formatMoney(totals.income_iva, baseCurrency)} testid="quarter-income-iva" />
        <MiniStat label={t("expenseIva")} value={formatMoney(totals.expense_iva, baseCurrency)} testid="quarter-expense-iva" />
        <MiniStat
          label={t("ivaBalance")}
          value={formatMoney(totals.iva_balance, baseCurrency)}
          accent={(totals.iva_balance || 0) > 0 ? "negative" : (totals.iva_balance || 0) < 0 ? "positive" : "muted"}
          testid="quarter-iva-balance"
        />
      </div>

      <div className="border-t border-[var(--border-color)] mt-4 pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <RatioCard
          label={t("foodCostPct")}
          pct={ratios.food_cost_pct}
          base={ratios.food_cost_base}
          revenue={ratios.revenue_base}
          baseCurrency={baseCurrency}
          formatMoney={formatMoney}
          colorClass={pillColor(ratios.food_cost_pct, 28, 35)}
          testid="ratio-food-cost"
          help={t("foodCostHelp")}
          lang={lang}
        />
        <RatioCard
          label={t("salaryPct")}
          pct={ratios.salary_pct}
          base={ratios.salary_base}
          revenue={ratios.revenue_base}
          baseCurrency={baseCurrency}
          formatMoney={formatMoney}
          colorClass={pillColor(ratios.salary_pct, 25, 35)}
          testid="ratio-salary"
          help={t("salaryHelp")}
          lang={lang}
        />
      </div>

      {/* Tabla de comparación.
          - Multi-year: si el usuario elige 2+ años → columnas = (mes, año).
          - Intra-year: si elige 2+ meses del mismo año → columnas = meses. */}
      {isMonthly && Array.isArray(compareYears) && compareYears.length >= 2 && Object.keys(monthlyDataByYear || {}).length >= 2 ? (
        <>
          <div className="flex justify-end mt-2 mb-1">
            <button
              type="button"
              onClick={async () => {
                try {
                  const monthsParam = (months && months.length) ? months.join(",") : undefined;
                  const yearsParam = compareYears.join(",");
                  const params = { years: yearsParam };
                  if (monthsParam) params.months = monthsParam;
                  const res = await api.get("/stats/yearly-comparison/export", {
                    params, responseType: "blob",
                  });
                  const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `comparativa-anual_${yearsParam.replace(/,/g, "-")}.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
              data-testid="yearly-comparison-xlsx-export"
              className="text-xs px-3 py-1 rounded border border-[var(--border-color)] bg-[var(--surface)] hover:bg-[var(--surface-hover)]"
              title={lang === "es" ? "Descargar como Excel" : "Download as Excel"}
            >
              {lang === "es" ? "Descargar Excel" : "Download Excel"}
            </button>
          </div>
          <YearComparisonTable
            data={monthlyDataByYear}
            months={months}
            monthNames={monthNames}
            baseCurrency={baseCurrency}
            formatMoney={formatMoney}
            lang={lang}
          />
        </>
      ) : isMonthly && Array.isArray(monthlyData?.per_month) && monthlyData.per_month.length >= 2 ? (
        <MonthlyComparisonTable
          perMonth={monthlyData.per_month}
          monthNames={monthNames}
          baseCurrency={baseCurrency}
          formatMoney={formatMoney}
          lang={lang}
        />
      ) : null}
    </Card>
  );
}

/**
 * Selector de años múltiples para modo mensual del dashboard. Cuando el
 * usuario selecciona 2+ años, el padre dispara N llamadas paralelas a
 * `/stats/monthly` (una por año) y `PeriodSummary` renderiza
 * `YearComparisonTable` en vez del comparativo intra-año.
 *
 * Se rinde en un `<details>` para no saturar la cabecera cuando no se usa.
 */
function MultiYearSelector({ current, selected, onChange, lang }) {
  // Últimos 5 años + el actual como opciones (rango pragmático).
  const options = Array.from({ length: 6 }, (_, i) => current - 4 + i);
  const set = new Set(selected || []);
  const toggle = (y) => {
    const next = new Set(set);
    if (next.has(y)) next.delete(y);
    else next.add(y);
    if (next.size === 0) next.add(current);
    onChange(Array.from(next).sort((a, b) => a - b));
  };
  return (
    <details className="inline-block" data-testid="multi-year-details">
      <summary className="cursor-pointer px-2.5 py-1 rounded border border-[var(--border-color)] bg-[var(--surface)] hover:bg-[var(--surface-hover)] list-none">
        {lang === "es" ? "Comparar años" : "Compare years"}
        {selected && selected.length >= 2 ? ` · ${selected.length}` : ""}
      </summary>
      <div className="absolute mt-1 z-10 bg-[var(--surface)] border border-[var(--border-color)] rounded-md shadow-lg p-2 space-y-1">
        {options.map((y) => (
          <label key={y} className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-[var(--surface-hover)] cursor-pointer">
            <input
              type="checkbox"
              checked={set.has(y)}
              onChange={() => toggle(y)}
              data-testid={`compare-year-${y}`}
              className="rounded border-[var(--border-color)]"
            />
            <span className="font-mono-num">{y}</span>
          </label>
        ))}
      </div>
    </details>
  );
}


/**
 * Tabla de comparación cruzada año × mes. Filas = KPIs (Ingresos, Gastos,
 * Beneficio, Beneficio%, IVA balance, Food cost %, Salarios %). Columnas =
 * combinaciones (mes, año) ordenadas: primero por mes, luego por año, para
 * que "Ene-2025 · Ene-2026 · Feb-2025 · Feb-2026" se lea de izquierda a derecha.
 */
function YearComparisonTable({ data, months, monthNames, baseCurrency, formatMoney, lang }) {
  const sortedYears = Object.keys(data || {}).map(Number).sort((a, b) => a - b);
  const sortedMonths = (months || []).slice().sort((a, b) => a - b);
  // Aplanamos a (year, month) preservando el orden mes-primero.
  const columns = [];
  for (const m of sortedMonths) {
    for (const y of sortedYears) {
      const yearData = data[y];
      const monthData = yearData?.per_month?.find((p) => p.month === m);
      if (monthData) columns.push({ year: y, month: m, snap: monthData });
    }
  }
  if (columns.length === 0) return null;

  const rows = [
    {
      key: "revenue_base",
      label: lang === "es" ? "Ingresos (base)" : "Revenue (net)",
      get: (s) => s.ratios.revenue_base,
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "expense_base",
      label: lang === "es" ? "Gastos (base)" : "Expenses (net)",
      get: (s) => s.totals.expense_base,
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "profit",
      label: lang === "es" ? "Beneficio" : "Profit",
      get: (s) => (s.ratios.revenue_base || 0) - (s.totals.expense_base || 0),
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "profit_pct",
      label: lang === "es" ? "% Beneficio" : "Profit %",
      get: (s) => {
        const rev = s.ratios.revenue_base || 0;
        return rev > 0 ? ((rev - (s.totals.expense_base || 0)) / rev) * 100 : null;
      },
      fmt: (v) => (v == null ? "—" : `${v.toFixed(1)}%`),
    },
    {
      key: "iva_balance",
      label: lang === "es" ? "Balance IVA" : "VAT balance",
      get: (s) => s.totals.iva_balance,
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "food_cost_pct",
      label: "Food cost %",
      get: (s) => s.ratios.food_cost_pct,
      fmt: (v) => (v == null ? "—" : `${v.toFixed(1)}%`),
    },
    {
      key: "salary_pct",
      label: lang === "es" ? "Salarios %" : "Salary %",
      get: (s) => s.ratios.salary_pct,
      fmt: (v) => (v == null ? "—" : `${v.toFixed(1)}%`),
    },
  ];

  return (
    <div className="mt-5 pt-4 border-t border-[var(--border-color)]" data-testid="year-comparison">
      <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-2">
        {lang === "es" ? "Comparación por meses (varios años)" : "Cross-year monthly comparison"}
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border-color)]">
              <th className="text-left font-medium py-2 pr-3 text-[var(--text-secondary)]">KPI</th>
              {columns.map((c) => (
                <th
                  key={`${c.month}-${c.year}`}
                  className="text-right font-medium py-2 px-2 text-[var(--text-secondary)] whitespace-nowrap"
                  data-testid={`col-${c.month}-${c.year}`}
                >
                  {monthNames[c.month - 1]}<br />
                  <span className="text-[10px] font-mono-num text-[var(--text-muted)]">{c.year}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-[var(--border-color)]/60 last:border-b-0">
                <td className="py-1.5 pr-3 text-[var(--text-secondary)] whitespace-nowrap">{row.label}</td>
                {columns.map((c) => (
                  <td
                    key={`${row.key}-${c.month}-${c.year}`}
                    className="py-1.5 px-2 text-right font-mono-num tabular-nums whitespace-nowrap"
                    data-testid={`cell-${row.key}-${c.month}-${c.year}`}
                  >
                    {row.fmt(row.get(c.snap))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function MonthlyComparisonTable({ perMonth, monthNames, baseCurrency, formatMoney, lang }) {
  const rows = [
    {
      key: "revenue_base",
      label: lang === "es" ? "Ingresos (base)" : "Revenue (net)",
      get: (p) => p.ratios.revenue_base,
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "expense_base",
      label: lang === "es" ? "Gastos (base)" : "Expenses (net)",
      get: (p) => p.totals.expense_base,
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "profit",
      label: lang === "es" ? "Beneficio" : "Profit",
      get: (p) => (p.ratios.revenue_base || 0) - (p.totals.expense_base || 0),
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "profit_pct",
      label: lang === "es" ? "% Beneficio" : "Profit %",
      get: (p) => {
        const rev = p.ratios.revenue_base || 0;
        return rev > 0 ? ((rev - (p.totals.expense_base || 0)) / rev) * 100 : null;
      },
      fmt: (v) => (v == null ? "—" : `${v.toFixed(1)}%`),
    },
    {
      key: "income_iva",
      label: lang === "es" ? "IVA repercutido" : "Output VAT",
      get: (p) => p.totals.income_iva,
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "expense_iva",
      label: lang === "es" ? "IVA soportado" : "Input VAT",
      get: (p) => p.totals.expense_iva,
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "iva_balance",
      label: lang === "es" ? "Balance IVA" : "VAT balance",
      get: (p) => p.totals.iva_balance,
      fmt: (v) => formatMoney(v, baseCurrency),
    },
    {
      key: "food_cost_pct",
      label: lang === "es" ? "Food cost %" : "Food cost %",
      get: (p) => p.ratios.food_cost_pct,
      fmt: (v) => (v == null ? "—" : `${v.toFixed(1)}%`),
    },
    {
      key: "salary_pct",
      label: lang === "es" ? "Salarios %" : "Salary %",
      get: (p) => p.ratios.salary_pct,
      fmt: (v) => (v == null ? "—" : `${v.toFixed(1)}%`),
    },
  ];

  return (
    <div className="mt-5 pt-4 border-t border-[var(--border-color)]" data-testid="monthly-comparison">
      <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-2">
        {lang === "es" ? "Comparación por meses" : "Month-by-month comparison"}
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border-color)]">
              <th className="text-left font-medium py-2 pr-3 text-[var(--text-secondary)]">
                {lang === "es" ? "KPI" : "KPI"}
              </th>
              {perMonth.map((p) => (
                <th key={p.month} className="text-right font-medium py-2 px-2 text-[var(--text-secondary)]" data-testid={`col-month-${p.month}`}>
                  {monthNames[p.month - 1]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-[var(--border-color)]/60 last:border-b-0">
                <td className="py-1.5 pr-3 text-[var(--text-secondary)]">{row.label}</td>
                {perMonth.map((p) => (
                  <td
                    key={p.month}
                    className="py-1.5 px-2 text-right font-mono-num tabular-nums"
                    data-testid={`cell-${row.key}-${p.month}`}
                  >
                    {row.fmt(row.get(p))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function MiniStat({ label, value, accent, testid }) {
  const accentClass = {
    positive: "text-[var(--status-positive)]",
    negative: "text-[var(--status-negative)]",
    muted: "text-[var(--text-muted)]",
  }[accent] || "text-[var(--text-primary)]";
  return (
    <div data-testid={testid} className="bg-[var(--surface-hover)]/40 border border-[var(--border-color)] rounded-md px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{label}</p>
      <p className={`font-mono-num font-medium ${accentClass}`}>{value}</p>
    </div>
  );
}

/**
 * Custom Recharts tooltip for the monthly Food-cost / Salary / Revenue chart.
 * Each row shows the % AND the underlying € amount so the user can eyeball
 * both signals without leaving the chart (previously the tooltip only showed
 * the %, forcing a lookup elsewhere).
 */
function RatiosTooltip({ active, payload, label, lang, t, formatMoney, baseCurrency }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload || {};
  const items = [
    { key: "food_cost_pct", pctVal: row.food_cost_pct, base: row.food_cost_base, name: t("foodCostPct"), color: "#C98A3A" },
    { key: "salary_pct",    pctVal: row.salary_pct,    base: row.salary_base,    name: t("salaryPct"),   color: "#3F5C7A" },
    { key: "revenue",       pctVal: null,              base: row.revenue_base,   name: t("income"),      color: "#2C4C3B" },
  ];
  return (
    <div
      className="bg-[var(--surface)] border border-[var(--border-color)] rounded-md shadow-lg px-3 py-2 text-xs"
      data-testid="ratios-tooltip"
    >
      <p className="font-medium text-[var(--text-primary)] mb-1.5">{label}</p>
      <div className="space-y-1">
        {items.map((it) => (
          <div key={it.key} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: it.color }} />
            <span className="text-[var(--text-secondary)] flex-1">{it.name}</span>
            <span className="font-mono-num tabular-nums text-[var(--text-primary)]">
              {it.pctVal != null && (
                <span className="font-semibold mr-2">{it.pctVal.toFixed(1)}%</span>
              )}
              <span className={it.pctVal != null ? "text-[var(--text-muted)]" : "font-semibold"}>
                {formatMoney(it.base || 0, baseCurrency)}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


function RatioCard({ label, pct, base, revenue, baseCurrency, formatMoney, colorClass, testid, help, lang }) {
  return (
    <div data-testid={testid} className="border border-[var(--border-color)] rounded-lg p-4 card-lift" title={help}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-[var(--text-muted)]">{label}</p>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
          {pct == null ? "—" : `${pct.toFixed(1)}%`}
        </span>
      </div>
      {/* Show both % (primary) and absolute amount (secondary) on the same
          row so the user reads e.g. "28.5% · €3,500" at a glance. */}
      <div className="flex items-baseline gap-3 flex-wrap">
        <p className="font-display text-2xl font-bold font-mono-num tracking-tight leading-none">
          {pct == null ? "—" : `${pct.toFixed(1)}%`}
        </p>
        <p
          className="font-mono-num text-lg text-[var(--text-secondary)] font-semibold leading-none"
          data-testid={`${testid}-amount`}
        >
          {formatMoney(base || 0, baseCurrency)}
        </p>
      </div>
      <p className="text-[11px] text-[var(--text-muted)] mt-2 font-mono-num">
        {lang === "es" ? "sobre " : "over "}
        <span className="font-medium">{formatMoney(revenue || 0, baseCurrency)}</span>
        <span className="ml-1 uppercase tracking-wider text-[10px]">
          {lang === "es" ? "base imponible" : "net (excl. VAT)"}
        </span>
      </p>
    </div>
  );
}

/**
 * (Year-over-year comparison and Prime Cost ratio removed per user request.)
 */

function UploadPaceBanner({ data, lang }) {
  if (!data || data.level === "ok") return null;
  const isCritical = data.level === "critical";
  const palette = isCritical
    ? "border-[var(--status-negative)]/40 bg-[var(--status-negative-bg)] text-[var(--status-negative)]"
    : "border-[var(--status-warning)]/40 bg-[var(--status-warning-bg)] text-[var(--status-warning)]";
  const heading = isCritical
    ? (lang === "es" ? "Faltan facturas por subir este mes" : "Missing invoices this month")
    : (lang === "es" ? "Ritmo de subida por debajo del promedio" : "Upload pace below average");
  return (
    <div
      data-testid="upload-pace-banner"
      className={`rounded-lg border px-4 py-3 flex items-start gap-3 ${palette}`}
    >
      <AlertTriangle size={18} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold tracking-tight text-sm">
          {heading}
        </p>
        <p className="text-xs text-[var(--text-secondary)] mt-1 leading-relaxed">
          {data.message}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1.5">
          {data.current_month} · {data.current_count} {lang === "es" ? "facturas" : "invoices"}
          {" · "}
          {lang === "es" ? "media 6m" : "6m avg"}: <span className="font-mono-num">{data.average_per_month}</span>
        </p>
      </div>
    </div>
  );
}



function TopExpensesCard({ lang, t }) {
  const { user } = useAuth();
  const baseCurrency = user?.base_currency || "EUR";
  const [period, setPeriod] = useState("month"); // month | cumulative | total
  const [months, setMonths] = useState(3);
  const [rows, setRows] = useState([]);
  const [periodLabel, setPeriodLabel] = useState("");
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stats/top-expenses", {
        params: { period, months, limit: 30 },
      });
      setRows(data.vendors || []);
      setPeriodLabel(data.period_label || "");
      setGrandTotal(data.grand_total || 0);
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period, months]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams({ period, limit: "30" });
      if (period === "cumulative") params.set("months", String(months));
      const res = await api.get(`/stats/top-expenses/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `top-gastos-${period}-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // toast comes from the top-level page if needed; keep silent here
    }
  };

  const titleSuffix = period === "month"
    ? (lang === "es" ? "este mes" : "this month")
    : period === "ytd"
      ? (lang === "es" ? "este año (YTD)" : "year-to-date")
      : period === "cumulative"
        ? (lang === "es" ? `últimos ${months} meses` : `last ${months} months`)
        : (lang === "es" ? "total" : "all-time");

  return (
    <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4" data-testid="top-expenses-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight">
            {lang === "es" ? "Top gastos" : "Top expenses"} · {titleSuffix}
          </h3>
          <p className="text-xs text-[var(--text-muted)]">
            {lang === "es"
              ? `${rows.length} proveedores · total ${formatMoney(grandTotal, baseCurrency)}${periodLabel ? ` · ${periodLabel}` : ""}`
              : `${rows.length} vendors · total ${formatMoney(grandTotal, baseCurrency)}${periodLabel ? ` · ${periodLabel}` : ""}`}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setPeriod("month")}
            data-testid="top-expenses-period-month"
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${period === "month" ? "bg-[var(--primary)] text-white" : "border border-[var(--border-color)] hover:bg-[var(--surface-hover)]"}`}
          >
            {lang === "es" ? "Mes" : "Month"}
          </button>
          <button
            onClick={() => setPeriod("cumulative")}
            data-testid="top-expenses-period-cumulative"
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${period === "cumulative" ? "bg-[var(--primary)] text-white" : "border border-[var(--border-color)] hover:bg-[var(--surface-hover)]"}`}
          >
            {lang === "es" ? "Acumulado" : "Cumulative"}
          </button>
          {period === "cumulative" && (
            <select
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value, 10))}
              className="px-2 py-1 rounded-md text-xs border border-[var(--border-color)] bg-[var(--surface)]"
              data-testid="top-expenses-months-select"
            >
              <option value={3}>3</option>
              <option value={6}>6</option>
              <option value={12}>12</option>
            </select>
          )}
          <button
            onClick={() => setPeriod("ytd")}
            data-testid="top-expenses-period-ytd"
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${period === "ytd" ? "bg-[var(--primary)] text-white" : "border border-[var(--border-color)] hover:bg-[var(--surface-hover)]"}`}
          >
            YTD
          </button>
          <button
            onClick={() => setPeriod("total")}
            data-testid="top-expenses-period-total"
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${period === "total" ? "bg-[var(--primary)] text-white" : "border border-[var(--border-color)] hover:bg-[var(--surface-hover)]"}`}
          >
            {lang === "es" ? "Total" : "Total"}
          </button>
          <button
            onClick={exportExcel}
            disabled={loading || rows.length === 0}
            data-testid="top-expenses-export-xlsx"
            className="px-2.5 py-1 rounded-md text-xs font-medium border border-[var(--border-color)] hover:bg-[var(--surface-hover)] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            title={lang === "es" ? "Exportar a Excel" : "Export to Excel"}
          >
            <Download size={12} />
            {lang === "es" ? "Excel" : "Excel"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="py-10 text-center text-sm text-[var(--text-muted)]">…</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="py-10 text-center text-sm text-[var(--text-muted)]">
          {lang === "es" ? "Sin gastos en este periodo." : "No expenses in this period."}
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="top-expenses-table">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              <tr className="border-b border-[var(--border-color)]">
                <th className="text-right py-2 px-2 w-10">#</th>
                <th className="text-left py-2 px-2">{lang === "es" ? "Proveedor" : "Vendor"}</th>
                <th className="text-right py-2 px-2">{lang === "es" ? "Facturas" : "Invoices"}</th>
                <th className="text-right py-2 px-2">{lang === "es" ? "Base" : "Net"}</th>
                <th className="text-right py-2 px-2">{lang === "es" ? "Total" : "Total"}</th>
                <th className="text-right py-2 px-2 w-16">%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const pct = grandTotal > 0 ? (r.total_amount / grandTotal) * 100 : 0;
                return (
                  <tr key={r.group_key} className="border-b border-[var(--border-color)] last:border-0 hover:bg-[var(--surface-hover)]" data-testid={`top-expense-row-${idx}`}>
                    <td className="py-2 px-2 text-right font-mono-num text-xs text-[var(--text-muted)]">{idx + 1}</td>
                    <td className="py-2 px-2">
                      <p className="font-medium truncate max-w-xs">{r.primary_name}</p>
                      {r.vendor_tax_id && (
                        <p className="text-[10px] font-mono text-[var(--text-muted)]">{r.vendor_tax_id}</p>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right font-mono-num">{r.invoice_count}</td>
                    <td className="py-2 px-2 text-right font-mono-num text-[var(--text-secondary)]">{formatMoney(r.total_subtotal, baseCurrency)}</td>
                    <td className="py-2 px-2 text-right font-mono-num font-semibold">{formatMoney(r.total_amount, baseCurrency)}</td>
                    <td className="py-2 px-2 text-right font-mono-num text-xs text-[var(--text-muted)]">{pct.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
